/**
 * Beat catalog, beat rows/cards and beat shelves (Framer SoundeeTracksBrowser + SoundeeTracksCarousel).
 * - <mrk-beat-catalog>: client-side filter (genre, mood, key, BPM range, price), sort, search, suggested tags,
 *   incremental reveal, list/grid layout, mobile filter sheet; state mirrored in the URL.
 * - <mrk-beat-shelf>: horizontal carousel arrows, optional best-selling order.
 * - Document-level: row "more" menus (license buttons: mrk-license-dialog.js; share: mrk-events.js).
 * Talks to other components only through the events in ./mrk-events.js.
 */
import { MRK_EVENTS, emit, on, beatFromElement } from './mrk-events.js';

const FACETS = ['genre', 'mood', 'key', 'price'];
const MORE_SORTS = ['oldest', 'bpm-asc', 'bpm-desc', 'price-asc', 'price-desc', 'title-asc'];
const SORTS = ['newest', 'top', 'random', 'relevance', ...MORE_SORTS];
const LAYOUT_KEY = 'mrk:catalog:layout';
const LINE_SEPARATOR = String.fromCharCode(0x2028); // stray character in some key values
const BPM_BANDS = [
  [0, 89],
  [90, 109],
  [110, 129],
  [130, 149],
  [150, 999],
];

/* ---------- Normalisation (store data is not uniform: "Beats W/ Hooks", "Bb min", "C♯ min / D♭ min") ---------- */

/** @param {string} value */
export function slugify(value) {
  return value
    .toLowerCase()
    .replace(/#/g, '-sharp')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** @param {string} value */
function titleCase(value) {
  return value.replace(/(^|[\s\-/(])([a-z])/g, (_, lead, letter) => lead + letter.toUpperCase());
}

/** @param {string} raw */
export function normalizeGenre(raw) {
  const value = raw
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\bw\/\s*/g, 'with ')
    .replace(/^r\s*&\s*b$|^rnb$/, 'r&b')
    .trim();
  if (!value) return null;
  const label = value === 'r&b' ? 'R&B' : titleCase(value);
  return { slug: slugify(value), label };
}

/** @param {string} raw */
export function normalizeMood(raw) {
  const value = raw.toLowerCase().replace(/\s+/g, ' ').trim();
  if (!value) return null;
  return { slug: slugify(value), label: titleCase(value) };
}

/** @param {string} raw */
export function normalizeKey(raw) {
  const value = raw
    .toLowerCase()
    .replaceAll(LINE_SEPARATOR, ' ')
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b')
    .split('/')[0]
    .replace(/\s+/g, ' ')
    .trim();
  if (!value || value === 'none') return null;
  const match = value.match(/^([a-g])\s*([#b]?)\s*(major|minor|maj|min|m)?/);
  if (!match) return null;
  const note = match[1].toUpperCase() + (match[2] || '');
  const quality = match[3] ? (match[3].startsWith('maj') ? 'Maj' : 'Min') : '';
  const label = quality ? `${note} ${quality}` : note;
  return { slug: slugify(label), label };
}

/** @param {string} raw */
export function normalizeBpm(raw) {
  const number = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

/* ---------- Small helpers ---------- */

/**
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timer;
  return (/** @type {Parameters<T>} */ ...args) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
}

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode) */
  }
}

/** @param {HTMLElement} root */
function firstFocusable(root) {
  return /** @type {HTMLElement | null} */ (
    root.querySelector('input:not([hidden]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
  );
}

/**
 * @typedef {Object} Row
 * @property {HTMLElement} el
 * @property {number} id
 * @property {number} index
 * @property {string} title
 * @property {{slug: string, label: string} | null} genre
 * @property {{slug: string, label: string} | null} mood
 * @property {{slug: string, label: string} | null} key
 * @property {{slug: string, label: string} | null} price
 * @property {number} bpm
 * @property {number} priceCents
 * @property {number} created
 * @property {string} haystack
 */

/* ---------- <mrk-beat-catalog> ---------- */

class MrkBeatCatalog extends HTMLElement {
  connectedCallback() {
    if (this.rows) return;
    this.list = /** @type {HTMLOListElement | null} */ (this.querySelector('[data-mrk-list]'));
    if (!this.list) return;

    this.pageSize = parseInt(this.dataset.mrkPageSize || '30', 10) || 30;
    this.bestSellingLoaded = this.dataset.mrkBestSelling === 'true';
    this.searchMode = this.dataset.mrkSearchMode === 'true';
    this.visibleCount = this.pageSize;
    /** @type {Map<number, number>} */
    this.randomKeys = new Map();

    // Rows beyond the first page arrive inert in a <template> (keeps the initial DOM small); they are
    // attached to the list only when a filter, sort or "load more" needs them.
    const rest = /** @type {HTMLTemplateElement | null} */ (this.list.querySelector('template[data-mrk-rest]'));
    const elements = [
      ...this.list.querySelectorAll(':scope > [data-mrk-row]'),
      ...(rest ? rest.content.querySelectorAll('[data-mrk-row]') : []),
    ];
    rest?.remove();
    /** @type {Row[]} */
    this.rows = elements.map((el, index) => this.readRow(/** @type {HTMLElement} */ (el), index));
    this.buildFacets();
    this.state = this.stateFromUrl(new URLSearchParams(window.location.search));

    this.renderOptions();
    this.renderTags();
    this.bindEvents();
    this.restoreLayout();
    this.apply({ emitQueue: false, writeUrl: false });
    // Playing from the list queues every filtered beat, including rows not revealed yet (read at click time by
    // queueFromElement), without replacing a saved player queue on page load.
    if (this.list) Object.assign(this.list, { mrkQueue: () => this.queueItems() });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.offPlayer?.();
    this.abort?.abort();
  }

  /** @param {HTMLElement} el @param {number} index @returns {Row} */
  readRow(el, index) {
    const d = el.dataset;
    const genre = normalizeGenre(d.mrkGenre || '');
    const mood = normalizeMood(d.mrkMood || '');
    const key = normalizeKey(d.mrkKey || '');
    const bpm = normalizeBpm(d.mrkBpm || '');
    const priceCents = parseInt(d.mrkPrice || '0', 10) || 0;
    const price = priceCents ? { slug: String(priceCents), label: d.mrkPriceLabel || '' } : null;
    const title = d.mrkTitle || '';
    const haystack = [title, genre?.label, mood?.label, key?.label, bpm ? `${bpm} bpm` : '']
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return {
      el,
      id: parseInt(d.mrkId || '0', 10),
      index,
      title,
      genre,
      mood,
      key,
      price,
      bpm,
      priceCents,
      created: parseInt(d.mrkCreated || '0', 10) || 0,
      haystack,
    };
  }

  buildFacets() {
    /** @type {Record<string, Map<string, {label: string, total: number}>>} */
    this.facets = {};
    for (const facet of FACETS) {
      const values = new Map();
      for (const row of this.rows) {
        const value = row[facet];
        if (!value) continue;
        const entry = values.get(value.slug) || { label: value.label, total: 0 };
        entry.total += 1;
        values.set(value.slug, entry);
      }
      const sorted = [...values.entries()].sort((a, b) =>
        facet === 'price' ? Number(a[0]) - Number(b[0]) : b[1].total - a[1].total || a[1].label.localeCompare(b[1].label)
      );
      this.facets[facet] = new Map(sorted);
    }
  }

  /** @param {URLSearchParams} params */
  stateFromUrl(params) {
    /** @type {Record<string, Set<string>>} */
    const sets = {};
    for (const facet of FACETS) {
      const known = this.facets[facet];
      sets[facet] = new Set(
        (params.get(facet) || '')
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value && known.has(value))
      );
    }
    const bpmMin = normalizeBpm(params.get('bpm_min') || '') || null;
    const bpmMax = normalizeBpm(params.get('bpm_max') || '') || null;
    const fallback = this.searchMode ? 'relevance' : 'newest';
    let sort = params.get('sort') || '';
    if (this.bestSellingLoaded && !sort) sort = 'top';
    if (!SORTS.includes(sort) || (sort === 'top' && !this.bestSellingLoaded) || (sort === 'relevance' && !this.searchMode)) {
      sort = fallback;
    }
    return {
      ...sets,
      bpmMin,
      bpmMax,
      q: this.searchMode ? '' : (params.get('q') || '').trim(),
      sort,
    };
  }

  /* ----- Matching ----- */

  /** @param {Row} row @param {string} [skip] facet to ignore (for option counts) */
  matches(row, skip) {
    const state = this.state;
    for (const facet of FACETS) {
      if (facet === skip) continue;
      const selected = state[facet];
      if (selected.size && !(row[facet] && selected.has(row[facet].slug))) return false;
    }
    if (skip !== 'bpm') {
      if (state.bpmMin && row.bpm < state.bpmMin) return false;
      if (state.bpmMax && row.bpm > state.bpmMax) return false;
    }
    if (state.q) {
      const terms = state.q.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every((term) => row.haystack.includes(term))) return false;
    }
    return true;
  }

  /** @param {Row[]} rows */
  sortRows(rows) {
    const sorted = [...rows];
    const byIndex = (/** @type {Row} */ a, /** @type {Row} */ b) => a.index - b.index;
    switch (this.state.sort) {
      case 'top':
      case 'relevance':
        return sorted.sort(byIndex);
      case 'oldest':
        return sorted.sort((a, b) => a.created - b.created || byIndex(a, b));
      case 'bpm-asc':
        return sorted.sort((a, b) => (a.bpm || 1e4) - (b.bpm || 1e4) || byIndex(a, b));
      case 'bpm-desc':
        return sorted.sort((a, b) => b.bpm - a.bpm || byIndex(a, b));
      case 'price-asc':
        return sorted.sort((a, b) => a.priceCents - b.priceCents || byIndex(a, b));
      case 'price-desc':
        return sorted.sort((a, b) => b.priceCents - a.priceCents || byIndex(a, b));
      case 'title-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'random': {
        if (!this.randomKeys.size) for (const row of this.rows) this.randomKeys.set(row.id, Math.random());
        return sorted.sort((a, b) => (this.randomKeys.get(a.id) || 0) - (this.randomKeys.get(b.id) || 0));
      }
      default:
        return sorted.sort((a, b) => b.created - a.created || byIndex(a, b));
    }
  }

  /* ----- Render ----- */

  /**
   * Filters and sorts every row, attaches the first page to the list and syncs controls, URL and player queue.
   * @param {{ emitQueue?: boolean, writeUrl?: boolean }} [options]
   */
  apply({ emitQueue = true, writeUrl = true } = {}) {
    const list = /** @type {HTMLOListElement} */ (this.list);
    this.matched = this.sortRows(this.rows.filter((row) => this.matches(row)));
    this.visibleCount = this.pageSize;

    list.replaceChildren(...this.matched.slice(0, this.visibleCount).map((row) => row.el));
    this.updateMore();

    const count = this.matched.length;
    const countNumber = this.querySelector('[data-mrk-count-number]');
    if (countNumber) countNumber.textContent = String(count);
    const sheetCount = this.querySelector('[data-mrk-sheet-count]');
    if (sheetCount) sheetCount.textContent = `(${count})`;
    const empty = this.querySelector('[data-mrk-empty]');
    if (empty) empty.toggleAttribute('hidden', count > 0);

    this.syncControls();
    if (writeUrl) this.writeUrl();
    if (emitQueue) this.emitQueue();
  }

  updateMore() {
    const moreWrap = this.querySelector('[data-mrk-more-wrap]');
    if (moreWrap) moreWrap.toggleAttribute('hidden', this.visibleCount >= (this.matched || []).length);
  }

  revealMore() {
    if (!this.matched || this.visibleCount >= this.matched.length) return;
    const start = this.visibleCount;
    this.visibleCount += this.pageSize;
    this.list?.append(...this.matched.slice(start, this.visibleCount).map((row) => row.el));
    this.updateMore();
  }

  /** Every filtered, playable beat in the current order (rendered or not). */
  queueItems() {
    const items = [];
    for (const row of this.matched || []) {
      const beat = beatFromElement(row.el.querySelector('[data-mrk-play]'));
      if (beat && !beat.sold && beat.audio) items.push(beat);
    }
    return items;
  }

  emitQueue() {
    emit(MRK_EVENTS.queueSet, { items: this.queueItems() });
  }

  writeUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    for (const facet of FACETS) {
      const values = [...this.state[facet]];
      if (values.length) params.set(facet, values.join(','));
      else params.delete(facet);
    }
    for (const [name, value] of [
      ['bpm_min', this.state.bpmMin],
      ['bpm_max', this.state.bpmMax],
    ]) {
      if (value) params.set(name, String(value));
      else params.delete(name);
    }
    if (!this.searchMode) {
      if (this.state.q) params.set('q', this.state.q);
      else params.delete('q');
    }
    if (this.state.sort === 'top') {
      params.delete('sort');
    } else {
      params.delete('sort_by');
      if (this.state.sort === 'newest' || this.state.sort === 'relevance') params.delete('sort');
      else params.set('sort', this.state.sort);
    }
    params.delete('page');
    const next = `${url.pathname}${params.toString() ? `?${params}` : ''}${url.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, '', next);
    }
  }

  /* ----- Options, tags and control state ----- */

  renderOptions() {
    for (const facet of FACETS) {
      const values = this.facets[facet];
      for (const container of this.querySelectorAll(`[data-mrk-options="${facet}"]`)) {
        const dd = container.closest('[data-mrk-dd], [data-mrk-sheet-facet]');
        if (!values.size) {
          dd?.setAttribute('hidden', '');
          continue;
        }
        const fragment = document.createDocumentFragment();
        for (const [slug, { label }] of values) {
          const option = document.createElement('label');
          option.className = 'mrk-opt';
          option.dataset.mrkOptLabel = label.toLowerCase();
          option.innerHTML =
            '<input class="mrk-opt__input" type="checkbox"><span class="mrk-opt__box" aria-hidden="true"></span>' +
            '<span class="mrk-opt__label"></span><span class="mrk-opt__count mrk-num"></span>';
          const input = /** @type {HTMLInputElement} */ (option.querySelector('input'));
          input.value = slug;
          input.dataset.mrkFacet = facet;
          /** @type {HTMLElement} */ (option.querySelector('.mrk-opt__label')).textContent = label;
          fragment.append(option);
        }
        container.replaceChildren(fragment);
      }
    }

    const bpms = this.rows.map((row) => row.bpm).filter(Boolean);
    for (const container of this.querySelectorAll('[data-mrk-bpm-presets]')) {
      if (!bpms.length) {
        container.closest('[data-mrk-dd], [data-mrk-sheet-facet]')?.setAttribute('hidden', '');
        continue;
      }
      const fragment = document.createDocumentFragment();
      for (const [min, max] of BPM_BANDS) {
        if (!bpms.some((bpm) => bpm >= min && bpm <= max)) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mrk-chip mrk-range__preset mrk-num';
        button.dataset.mrkBpmPreset = `${min || ''}-${max < 999 ? max : ''}`;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = min === 0 ? `< ${max + 1}` : max >= 999 ? `${min}+` : `${min}–${max}`;
        fragment.append(button);
      }
      container.replaceChildren(fragment);
    }
  }

  renderTags() {
    const wrap = this.querySelector('[data-mrk-tags-wrap]');
    const list = this.querySelector('[data-mrk-tags-list]');
    if (!wrap || !list) return;
    let tags = (this.dataset.mrkTags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    if (!tags.length) {
      const genres = [...this.facets.genre.values()].slice(0, 5).map((v) => v.label.toLowerCase());
      const moods = [...this.facets.mood.values()].slice(0, 8).map((v) => v.label.toLowerCase());
      tags = [...genres, ...moods];
    }
    if (!tags.length) return;
    list.replaceChildren(
      ...tags.map((tag) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mrk-chip mrk-catalog__tag';
        button.dataset.mrkTag = tag;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = tag;
        return button;
      })
    );
    wrap.removeAttribute('hidden');
    this.updateTagsArrow();
  }

  updateTagsArrow() {
    const list = this.querySelector('[data-mrk-tags-list]');
    const next = this.querySelector('[data-mrk-tags-next]');
    if (!list || !next) return;
    const overflowing = list.scrollWidth - list.clientWidth - list.scrollLeft > 4;
    next.classList.toggle('mrk-catalog__tags-next--visible', overflowing);
  }

  syncControls() {
    const state = this.state;
    // Checkboxes (desktop dropdowns + mobile sheet share state)
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[data-mrk-facet]'))) {
      input.checked = state[input.dataset.mrkFacet || '']?.has(input.value) || false;
    }
    // Faceted counts: rows that would match if this facet's own selection were ignored
    for (const facet of FACETS) {
      /** @type {Map<string, number>} */
      const counts = new Map();
      for (const row of this.rows) {
        if (row[facet] && this.matches(row, facet)) counts.set(row[facet].slug, (counts.get(row[facet].slug) || 0) + 1);
      }
      for (const option of this.querySelectorAll(`[data-mrk-options="${facet}"] .mrk-opt`)) {
        const input = /** @type {HTMLInputElement} */ (option.querySelector('input'));
        const count = counts.get(input.value) || 0;
        /** @type {HTMLElement} */ (option.querySelector('.mrk-opt__count')).textContent = String(count);
        option.classList.toggle('mrk-opt--empty', count === 0 && !input.checked);
      }
    }
    // BPM inputs and presets
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('[data-mrk-bpm-min]'))) {
      if (document.activeElement !== input) input.value = state.bpmMin ? String(state.bpmMin) : '';
    }
    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('[data-mrk-bpm-max]'))) {
      if (document.activeElement !== input) input.value = state.bpmMax ? String(state.bpmMax) : '';
    }
    const bpmValue = `${state.bpmMin || ''}-${state.bpmMax || ''}`;
    for (const preset of this.querySelectorAll('[data-mrk-bpm-preset]')) {
      preset.setAttribute('aria-pressed', String(preset.getAttribute('data-mrk-bpm-preset') === bpmValue));
    }
    // Active counts on triggers
    const activeByFacet = {
      genre: state.genre.size,
      mood: state.mood.size,
      key: state.key.size,
      price: state.price.size,
      bpm: state.bpmMin || state.bpmMax ? 1 : 0,
    };
    let totalActive = 0;
    for (const [facet, count] of Object.entries(activeByFacet)) {
      totalActive += count;
      for (const holder of this.querySelectorAll(`[data-mrk-dd="${facet}"], [data-mrk-sheet-facet="${facet}"]`)) {
        const badge = holder.querySelector('[data-mrk-dd-count]');
        if (badge) {
          badge.textContent = String(count);
          badge.toggleAttribute('hidden', count === 0);
        }
      }
    }
    this.querySelector('.mrk-catalog__clear-all')?.toggleAttribute('hidden', totalActive === 0 && !state.q);
    const pillCount = this.querySelector('[data-mrk-active-count]');
    if (pillCount) {
      pillCount.textContent = String(totalActive);
      pillCount.toggleAttribute('hidden', totalActive === 0);
    }
    // Sort chips
    for (const button of this.querySelectorAll('[data-mrk-sort-value]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-mrk-sort-value') === state.sort));
    }
    const moreTrigger = this.querySelector('[data-mrk-sort-more]');
    const moreLabel = this.querySelector('[data-mrk-sort-more-label]');
    if (moreTrigger && moreLabel) {
      const active = MORE_SORTS.includes(state.sort);
      moreTrigger.classList.toggle('mrk-sort-chip--active', active);
      const option = this.querySelector(`[data-mrk-dd="sort"] [data-mrk-sort-value="${state.sort}"]`);
      moreLabel.textContent = active && option ? option.textContent : moreLabel.getAttribute('data-mrk-default');
    }
    // Search + tags
    const q = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mrk-q]'));
    if (q && document.activeElement !== q) q.value = state.q;
    for (const tag of this.querySelectorAll('[data-mrk-tag]')) {
      tag.setAttribute('aria-pressed', String(tag.getAttribute('data-mrk-tag')?.toLowerCase() === state.q.toLowerCase()));
    }
    // Category cards reflect the active filter
    for (const link of /** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll('[data-mrk-cat]'))) {
      const target = this.parseSamePageLink(link);
      if (target) link.toggleAttribute('aria-current', this.sameFilters(target, state));
    }
  }

  /** @param {HTMLAnchorElement} link */
  parseSamePageLink(link) {
    try {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return null;
      return this.stateFromUrl(url.searchParams);
    } catch {
      return null;
    }
  }

  /** @param {any} a @param {any} b */
  sameFilters(a, b) {
    const hasAny = FACETS.some((facet) => a[facet].size) || a.bpmMin || a.bpmMax || a.q;
    if (!hasAny) return false;
    return (
      FACETS.every((facet) => a[facet].size === b[facet].size && [...a[facet]].every((v) => b[facet].has(v))) &&
      a.bpmMin === b.bpmMin &&
      a.bpmMax === b.bpmMax &&
      a.q === b.q
    );
  }

  clearAll() {
    for (const facet of FACETS) this.state[facet].clear();
    this.state.bpmMin = null;
    this.state.bpmMax = null;
    this.state.q = '';
    this.apply();
  }

  /** @param {string} facet */
  clearFacet(facet) {
    if (facet === 'bpm') {
      this.state.bpmMin = null;
      this.state.bpmMax = null;
    } else {
      this.state[facet]?.clear();
    }
    this.apply();
  }

  /** @param {string} sort */
  setSort(sort) {
    if (!SORTS.includes(sort)) return;
    if (sort === 'top' && !this.bestSellingLoaded) {
      // Best-selling order comes from Shopify: load the listed collection (not /collections, whose sort_by
      // can't apply to it) with sort_by, keeping the filters.
      this.writeUrl();
      const url = new URL(window.location.href);
      if (this.dataset.mrkCollectionUrl) url.pathname = this.dataset.mrkCollectionUrl;
      url.searchParams.delete('sort');
      url.searchParams.set('sort_by', 'best-selling');
      window.location.assign(url.toString());
      return;
    }
    if (sort === 'random') this.randomKeys.clear();
    this.state.sort = sort;
    this.apply();
  }

  /* ----- Layout ----- */

  restoreLayout() {
    this.setLayout(readStorage(LAYOUT_KEY) === 'grid' ? 'grid' : 'list', false);
  }

  /** @param {'list' | 'grid'} layout @param {boolean} [persist] */
  setLayout(layout, persist = true) {
    this.list?.classList.toggle('mrk-catalog__list--grid', layout === 'grid');
    const toggle = this.querySelector('[data-mrk-layout-toggle]');
    toggle?.setAttribute('aria-pressed', String(layout === 'grid'));
    if (persist) writeStorage(LAYOUT_KEY, layout);
  }

  /* ----- Dropdowns ----- */

  /** @param {Element} dd */
  openDropdown(dd) {
    this.closeDropdowns(dd);
    const trigger = /** @type {HTMLElement} */ (dd.querySelector('[data-mrk-dd-trigger]'));
    const panel = /** @type {HTMLElement} */ (dd.querySelector('[data-mrk-dd-panel]'));
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    dd.classList.add('mrk-dd--open');
    const rect = panel.getBoundingClientRect();
    panel.classList.toggle('mrk-dd__panel--flip', rect.right > document.documentElement.clientWidth - 8);
    firstFocusable(panel)?.focus({ preventScroll: true });
  }

  /** @param {Element} [except] @param {boolean} [restoreFocus] */
  closeDropdowns(except, restoreFocus = false) {
    for (const dd of this.querySelectorAll('[data-mrk-dd].mrk-dd--open')) {
      if (dd === except) continue;
      const trigger = /** @type {HTMLElement} */ (dd.querySelector('[data-mrk-dd-trigger]'));
      const panel = /** @type {HTMLElement} */ (dd.querySelector('[data-mrk-dd-panel]'));
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      dd.classList.remove('mrk-dd--open');
      const search = /** @type {HTMLInputElement | null} */ (panel.querySelector('[data-mrk-dd-search]'));
      if (search && search.value) {
        search.value = '';
        this.filterOptions(search);
      }
      if (restoreFocus) trigger.focus();
    }
  }

  /** @param {HTMLInputElement} search */
  filterOptions(search) {
    const term = search.value.trim().toLowerCase();
    const panel = search.closest('[data-mrk-dd-panel]');
    for (const option of panel?.querySelectorAll('.mrk-opt') || []) {
      option.toggleAttribute('hidden', Boolean(term) && !(/** @type {HTMLElement} */ (option).dataset.mrkOptLabel || '').includes(term));
    }
  }

  /* ----- Events ----- */

  bindEvents() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);

      const trigger = target.closest('[data-mrk-dd-trigger]');
      if (trigger) {
        const dd = /** @type {Element} */ (trigger.closest('[data-mrk-dd]'));
        if (dd.classList.contains('mrk-dd--open')) this.closeDropdowns();
        else this.openDropdown(dd);
        return;
      }

      const sortButton = target.closest('[data-mrk-sort-value]');
      if (sortButton) {
        this.closeDropdowns(undefined, Boolean(sortButton.closest('[data-mrk-dd]')));
        this.setSort(sortButton.getAttribute('data-mrk-sort-value') || 'newest');
        return;
      }

      const clear = target.closest('[data-mrk-clear]');
      if (clear) {
        this.clearFacet(clear.getAttribute('data-mrk-clear') || '');
        return;
      }

      if (target.closest('[data-mrk-clear-all]')) {
        this.clearAll();
        return;
      }

      const preset = target.closest('[data-mrk-bpm-preset]');
      if (preset) {
        const [min, max] = (preset.getAttribute('data-mrk-bpm-preset') || '-').split('-');
        const pressed = preset.getAttribute('aria-pressed') === 'true';
        this.state.bpmMin = pressed ? null : normalizeBpm(min) || null;
        this.state.bpmMax = pressed ? null : normalizeBpm(max) || null;
        this.apply();
        return;
      }

      const tag = target.closest('[data-mrk-tag]');
      if (tag) {
        const value = tag.getAttribute('data-mrk-tag') || '';
        this.state.q = this.state.q.toLowerCase() === value.toLowerCase() ? '' : value;
        this.apply();
        return;
      }

      if (target.closest('[data-mrk-tags-next]')) {
        const list = this.querySelector('[data-mrk-tags-list]');
        list?.scrollBy({ left: list.clientWidth * 0.6, behavior: 'smooth' });
        return;
      }

      if (target.closest('[data-mrk-more]')) {
        this.revealMore();
        return;
      }

      if (target.closest('[data-mrk-layout-toggle]')) {
        this.setLayout(this.list?.classList.contains('mrk-catalog__list--grid') ? 'list' : 'grid');
        return;
      }

      const cat = /** @type {HTMLAnchorElement | null} */ (target.closest('[data-mrk-cat]'));
      if (cat && !(event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey))) {
        const next = this.parseSamePageLink(cat);
        if (next) {
          event.preventDefault();
          const keepSort = this.state.sort;
          this.state = this.sameFilters(next, this.state) ? this.stateFromUrl(new URLSearchParams()) : next;
          this.state.sort = keepSort;
          this.apply();
        }
        return;
      }

      if (target.closest('[data-mrk-sheet-open]')) {
        /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-mrk-sheet]'))?.showModal();
        return;
      }

      const sheet = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-mrk-sheet]'));
      if (sheet && (target === sheet || target.closest('[data-mrk-sheet-close]'))) {
        sheet.close();
      }
    });

    this.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      const facet = input.dataset.mrkFacet;
      if (facet && this.state[facet]) {
        if (input.checked) this.state[facet].add(input.value);
        else this.state[facet].delete(input.value);
        this.apply();
        return;
      }
      if (input.matches('[data-mrk-bpm-min], [data-mrk-bpm-max]')) this.readBpm(input);
    });

    const onBpmInput = debounce((/** @type {HTMLInputElement} */ input) => this.readBpm(input), 400);
    const onQuery = debounce((/** @type {string} */ value) => {
      this.state.q = value.trim();
      this.apply();
    }, 150);

    this.addEventListener('input', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (input.matches('[data-mrk-q]')) onQuery(input.value);
      else if (input.matches('[data-mrk-dd-search]')) this.filterOptions(input);
      else if (input.matches('[data-mrk-bpm-min], [data-mrk-bpm-max]')) onBpmInput(input);
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.querySelector('[data-mrk-dd].mrk-dd--open')) {
        event.preventDefault();
        event.stopPropagation();
        this.closeDropdowns(undefined, true);
      }
      if (event.key === 'Enter' && /** @type {HTMLElement} */ (event.target).matches('[data-mrk-q]')) {
        event.preventDefault();
      }
    });

    this.addEventListener('focusout', (event) => {
      const next = /** @type {Node | null} */ (event.relatedTarget);
      const dd = /** @type {HTMLElement} */ (event.target).closest?.('[data-mrk-dd].mrk-dd--open');
      if (dd && next && !dd.contains(next)) this.closeDropdowns();
    });

    document.addEventListener(
      'pointerdown',
      (event) => {
        const open = this.querySelector('[data-mrk-dd].mrk-dd--open');
        if (open && !open.contains(/** @type {Node} */ (event.target))) this.closeDropdowns();
      },
      { signal }
    );

    this.querySelector('[data-mrk-tags-list]')?.addEventListener('scroll', () => this.updateTagsArrow(), { passive: true });
    window.addEventListener('resize', debounce(() => this.updateTagsArrow(), 150), { signal, passive: true });

    const moreWrap = this.querySelector('[data-mrk-more-wrap]');
    if (moreWrap && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.revealMore();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(moreWrap);
    }

    this.offPlayer = on(MRK_EVENTS.playerState, (event) => {
      const id = event.detail?.id;
      for (const row of /** @type {Row[]} */ (this.rows)) row.el.classList.toggle('mrk-beat-row--current', row.id === id);
    });
  }

  /** @param {HTMLInputElement} input */
  readBpm(input) {
    const value = normalizeBpm(input.value) || null;
    if (input.matches('[data-mrk-bpm-min]')) this.state.bpmMin = value;
    else this.state.bpmMax = value;
    this.apply();
  }
}

/* ---------- <mrk-beat-shelf> ---------- */

class MrkBeatShelf extends HTMLElement {
  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('[data-mrk-track]'));
    if (!this.track || this.abort) return;
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.prev = this.querySelector('[data-mrk-prev]');
    this.next = this.querySelector('[data-mrk-next]');
    this.prev?.addEventListener('click', () => this.scrollByPage(-1), { signal });
    this.next?.addEventListener('click', () => this.scrollByPage(1), { signal });
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });
    window.addEventListener('resize', debounce(() => this.updateArrows(), 150), { passive: true, signal });
    this.updateArrows();
    if (this.dataset.mrkBestSelling) this.orderByBestSelling(this.dataset.mrkBestSelling);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = undefined;
  }

  /** @param {1 | -1} direction */
  scrollByPage(direction) {
    const track = /** @type {HTMLElement} */ (this.track);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
  }

  updateArrows() {
    const track = /** @type {HTMLElement} */ (this.track);
    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    this.prev?.toggleAttribute('hidden', atStart);
    this.next?.toggleAttribute('hidden', atEnd);
  }

  /**
   * Shopify only exposes best-selling order on collection URLs, so read it from the catalog section rendered
   * through the Section Rendering API, then reorder the (pre-rendered) cards and reveal the first `limit`.
   * @param {string} collectionUrl
   */
  async orderByBestSelling(collectionUrl) {
    const track = /** @type {HTMLElement} */ (this.track);
    const limit = parseInt(this.dataset.mrkLimit || '12', 10);
    const cacheKey = `mrk:best-selling:${collectionUrl}`;
    /** @type {string[] | null} */
    let ids = null;
    try {
      ids = JSON.parse(window.sessionStorage.getItem(cacheKey) || 'null');
    } catch {
      ids = null;
    }
    if (!ids) {
      try {
        const response = await fetch(`${collectionUrl}?sort_by=best-selling&section_id=mrk-beat-catalog`);
        if (!response.ok) return;
        const html = await response.text();
        ids = [...html.matchAll(/data-mrk-row\s+data-mrk-id="(\d+)"/g)].map((match) => match[1]);
        try {
          window.sessionStorage.setItem(cacheKey, JSON.stringify(ids));
        } catch {
          /* storage unavailable */
        }
      } catch {
        return;
      }
    }
    const items = [...track.children];
    const rank = new Map(ids.map((id, i) => [id, i]));
    const rankOf = (/** @type {Element} */ item) =>
      rank.get(item.querySelector('[data-mrk-card]')?.getAttribute('data-mrk-id') || '') ?? Number.MAX_SAFE_INTEGER;
    items.sort((a, b) => rankOf(a) - rankOf(b));
    items.forEach((item, i) => item.toggleAttribute('hidden', i >= limit));
    track.replaceChildren(...items);
    this.updateArrows();
  }
}

/* ---------- Document-level behaviour for rows and cards anywhere on the page ---------- */

/** @param {HTMLElement} [except] */
function closeMenus(except) {
  for (const menu of document.querySelectorAll('[data-mrk-menu].mrk-beat-row__menu--open')) {
    if (menu === except) continue;
    menu.classList.remove('mrk-beat-row__menu--open');
    menu.querySelector('[data-mrk-menu-list]')?.setAttribute('hidden', '');
    menu.querySelector('[data-mrk-menu-toggle]')?.setAttribute('aria-expanded', 'false');
  }
}

if (!window.mrkCatalogBound) {
  window.mrkCatalogBound = true;

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const toggle = target.closest('[data-mrk-menu-toggle]');
    if (toggle) {
      const menu = /** @type {HTMLElement} */ (toggle.closest('[data-mrk-menu]'));
      const open = !menu.classList.contains('mrk-beat-row__menu--open');
      closeMenus(menu);
      menu.classList.toggle('mrk-beat-row__menu--open', open);
      menu.querySelector('[data-mrk-menu-list]')?.toggleAttribute('hidden', !open);
      toggle.setAttribute('aria-expanded', String(open));
      if (open) /** @type {HTMLElement | null} */ (menu.querySelector('[role="menuitem"]'))?.focus();
      return;
    }

    // Sharing itself is handled once, site-wide, in mrk-events.js.
    if (target.closest('[data-mrk-share]')) {
      closeMenus();
      return;
    }

    if (!target.closest('[data-mrk-menu]')) closeMenus();
  });

  document.addEventListener('keydown', (event) => {
    const menu = /** @type {HTMLElement | null} */ (document.querySelector('[data-mrk-menu].mrk-beat-row__menu--open'));
    if (!menu) return;
    const items = /** @type {HTMLElement[]} */ ([...menu.querySelectorAll('[role="menuitem"]')]);
    const index = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (event.key === 'Escape') {
      closeMenus();
      /** @type {HTMLElement | null} */ (menu.querySelector('[data-mrk-menu-toggle]'))?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Tab') {
      closeMenus();
    }
  });
}

if (!customElements.get('mrk-beat-catalog')) customElements.define('mrk-beat-catalog', MrkBeatCatalog);
if (!customElements.get('mrk-beat-shelf')) customElements.define('mrk-beat-shelf', MrkBeatShelf);
